import { getChartOptions } from "./chartConfig";
import {
  convertPeerToNationalAverage,
  filterHerdGoals,
  convertZerosToNull,
  removeDuplicateSeries,
  filterWithDataProperty,
  processDaysOpenData,
  shouldConvertPeerToNational,
} from "./chartDataProcessors";
import {
  CHART_IDS,
  AXIS_LABELS,
  WEEK_TICK_VALUES,
} from "../constants/chartConstants";

/**
 * Builds all chart series and options from graph data
 */
export const buildGraphListArray = (params) => {
  const {
    graphResult,
    curveChart,
    reportTab,
    useavgdailymilkflag,
    reportResponse,
    lactationcurvemaxy,
    lactationcurveminy,
    useFullMilkWeeks,
    showFullMilkWeeksToggle,
  } = params;

  const graphListArray = [];
  const graphObject =
    Object.keys(graphResult).length === 0 && graphResult.constructor === Object;

  const chartType = reportTab === "snapsix" ? "line" : "bar";
  const chartLabelColor = chartType === "line" ? undefined : ["#333"];

  const convertPeerToNational = shouldConvertPeerToNational(
    reportTab,
    reportResponse?.Peer_Footer_Definition,
  );

  // Determine axis labels
  let xaxislabel = AXIS_LABELS.DAYS_IN_MILK;
  if (
    reportTab !== "benchmark" &&
    (useavgdailymilkflag || reportResponse?.Use_Avg_Daily_Milk)
  ) {
    xaxislabel = AXIS_LABELS.WEEKS_IN_MILK;
  }

  let yaxislabel = AXIS_LABELS.TEST_DAY_MILK;
  if (
    useavgdailymilkflag ||
    (reportTab !== "benchmark" && reportResponse?.Use_Avg_Daily_Milk)
  ) {
    yaxislabel = AXIS_LABELS.WEEKLY_MILK;
  }

  const baseParams = {
    chartType,
    chartLabelColor,
    reportTab,
    useavgdailymilkflag,
    reportResponse,
    lactationcurvemaxy,
    lactationcurveminy,
  };

  // Energy Corrected Milk
  let ecmSeries = graphObject ? "" : graphResult.ecm_data[0].ECM_data;
  if (reportTab === "snapsix" && ecmSeries) {
    const filtered = filterWithDataProperty(ecmSeries);
    const { filtered: noGoals } = filterHerdGoals(filtered);
    ecmSeries = convertZerosToNull(noGoals);
  }
  ecmSeries = convertPeerToNationalAverage(ecmSeries, convertPeerToNational);

  const ecmOptions = getChartOptions({
    ...baseParams,
    category: graphObject ? "" : graphResult.ecm_data[0].categories,
    id: CHART_IDS.ECM,
    name: "Energy Corrected Milk Chart",
    xaxistext: "",
    yaxistext: AXIS_LABELS.ENERGY_CORRECTED_MILK,
    graphdataresponse: ecmSeries,
  });

  graphListArray.push({
    name: "Energy Corrected Milk",
    id: CHART_IDS.ECM,
    option: ecmOptions,
    series: ecmSeries,
  });

  // Skip other charts for culling history
  if (reportTab === "cullinghistory") {
    return graphListArray;
  }

  // Milk Production (Test Day or Weekly)
  const milkChartId = useavgdailymilkflag
    ? CHART_IDS.WEEKLY_MILK
    : CHART_IDS.TEST_DAY_MILK;

  const milkData = useavgdailymilkflag
    ? graphResult.weekly_milk_data[0]
    : graphResult.test_day_milk_data[0];

  const milkKey = useavgdailymilkflag
    ? "Latest_Weekly_Milk_data"
    : "Test_Day_Milk_data";

  // Milk Production chart y-axis is always based on useavgdailymilkflag
  const milkYAxisLabel = useavgdailymilkflag
    ? AXIS_LABELS.WEEKLY_MILK
    : AXIS_LABELS.TEST_DAY_MILK;

  let milkSeries = graphObject ? "" : milkData[milkKey];
  milkSeries = convertPeerToNationalAverage(milkSeries, convertPeerToNational);

  const milkOptions = getChartOptions({
    ...baseParams,
    category: graphObject ? "" : milkData.categories,
    id: milkChartId,
    name: "Milk Production Chart",
    xaxistext: "",
    yaxistext: milkYAxisLabel,
    graphdataresponse: milkSeries,
  });

  graphListArray.push({
    name: "Milk Production",
    id: milkChartId,
    series: milkSeries,
    option: milkOptions,
  });

  // Lactation Charts (1, 2, 3+)
  const lactationNumbers = [1, 2, 3];
  const lactationIds = [
    CHART_IDS.LACTATION_1,
    CHART_IDS.LACTATION_2,
    CHART_IDS.LACTATION_3,
  ];

  lactationNumbers.forEach((num, idx) => {
    const lactKey = `lacation_${num}_data`;
    const lactDataKey = `Lactation_${num}_data`;

    let lactSeries = graphObject
      ? ""
      : filterWithDataProperty(graphResult[lactKey][0][lactDataKey]);

    const { filtered: noGoals } = filterHerdGoals(lactSeries);
    lactSeries = removeDuplicateSeries(noGoals);
    lactSeries = convertZerosToNull(lactSeries);
    lactSeries = convertPeerToNationalAverage(
      lactSeries,
      convertPeerToNational,
    );

    let lactCategories = graphObject ? "" : graphResult[lactKey][0].categories;

    // Apply same filtering as lactation curve for DC305/DPLAN herds
    // Only apply when using weeks (not days in milk)
    // Exclude benchmark report only (include subherdcomparison to match snapshot behavior)
    if (
      showFullMilkWeeksToggle &&
      !useFullMilkWeeks &&
      reportTab !== "benchmark" &&
      (useavgdailymilkflag || reportResponse?.Use_Avg_Daily_Milk) &&
      Array.isArray(lactCategories)
    ) {
      const allowedWeeks = [2, 4, 8, 15, 21, 25, 32, 37];

      // Convert categories to numeric if needed
      const numericCategories = lactCategories.map((cat) => {
        if (typeof cat === "string" && cat.startsWith("Week ")) {
          const weekNumber = parseInt(cat.replace("Week ", ""), 10);
          return !isNaN(weekNumber) && weekNumber >= 1 && weekNumber <= 44
            ? weekNumber
            : cat;
        }
        return cat;
      });

      const originalCategories = numericCategories;

      // Filter categories to only allowed weeks
      lactCategories = numericCategories.filter((week) =>
        allowedWeeks.includes(week),
      );

      // Filter series data to match filtered categories and convert to {x, y} format for numeric spacing
      if (lactSeries && Array.isArray(lactSeries)) {
        lactSeries = lactSeries.map((series) => {
          if (series.data && Array.isArray(series.data)) {
            const filteredData = [];
            series.data.forEach((value, idx) => {
              if (allowedWeeks.includes(originalCategories[idx])) {
                filteredData.push({
                  x: originalCategories[idx],
                  y: value,
                });
              }
            });

            return {
              ...series,
              data: filteredData,
            };
          }
          return series;
        });
      }
    }

    const lactOptions = getChartOptions({
      ...baseParams,
      chartType: "line",
      category: lactCategories,
      id: lactationIds[idx],
      name: `Lactation ${num === 3 ? "3+" : num} Chart`,
      xaxistext: xaxislabel,
      yaxistext: yaxislabel,
      graphdataresponse: lactSeries,
      useFilteredWeeks:
        showFullMilkWeeksToggle &&
        !useFullMilkWeeks &&
        reportTab !== "benchmark" &&
        (useavgdailymilkflag || reportResponse?.Use_Avg_Daily_Milk),
    });

    graphListArray.push({
      name: `${num === 3 ? "3rd+" : num === 2 ? "2nd" : "1st"} Lactation`,
      id: lactationIds[idx],
      series: lactSeries,
      option: lactOptions,
    });
  });

  // Remaining bar charts
  const additionalCharts = [
    {
      name: "Percent Pregnant in Herd",
      id: CHART_IDS.HERD_DATA,
      dataPath: "percent_pregnant_in_herd_data",
      seriesKey: "Reproduction_data",
      yaxis: "Percent Pregnant in Herd",
    },
    {
      name: "1st Calf Heifer Profiles",
      id: CHART_IDS.CALF_HEIFER,
      dataPath: "first_calf_heifer_profiles_data",
      seriesKey: "HerdInventory_data",
      yaxis: "Percent Heifers",
    },
    {
      name: "Days Open",
      id: CHART_IDS.DAYS_OPEN,
      dataPath: "days_open_data",
      seriesKey: "Reproduction_data",
      yaxis: "Days Open",
      customProcess: true,
    },
    {
      name: "Percent Fat",
      id: CHART_IDS.PERCENT_FAT,
      dataPath: "percent_fat_data",
      seriesKey: "Percent_Fat_data",
      yaxis: "Fat(%)",
    },
    {
      name: "Somatic Cell",
      id: CHART_IDS.SOMATIC_CELL,
      dataPath: "somatic_cell_count",
      seriesKey: "Milk_Quality_data",
      xaxis: "Linear Score",
      yaxis: "Percent",
    },
    {
      name: "Percent Protein",
      id: CHART_IDS.PERCENT_PROTEIN,
      dataPath: "percent_protein",
      seriesKey: "Percent_Protein_data",
      yaxis: "Protein(%)",
    },
    {
      name: "Total lbs fat+protein",
      id: CHART_IDS.TOTAL_LBS,
      dataPath: "lbs_fat_protein_data",
      seriesKey: "ComponentAnalysis_data",
      yaxis: "Fat+Protein, lbs",
    },
    {
      name: "Average DIM",
      id: CHART_IDS.AVG_DIM,
      dataPath: "average_dim",
      seriesKey: "Average_DIM_data",
      yaxis: "DIM",
    },
    {
      name: "Calving Intervals",
      id: CHART_IDS.CALVING_INTERVAL,
      dataPath: "calving_interval",
      seriesKey: "Reproduction_data",
      yaxis: "Days",
    },
  ];

  additionalCharts.forEach((chart) => {
    let series = graphObject
      ? ""
      : graphResult[chart.dataPath][0][chart.seriesKey];
    let chartCategories = graphObject
      ? ""
      : graphResult[chart.dataPath][0].categories;

    if (chart.customProcess && chart.id === CHART_IDS.DAYS_OPEN) {
      const processed = processDaysOpenData(
        series,
        graphResult[chart.dataPath][0].categories,
        reportTab,
      );
      series = processed.series;
      chartCategories = processed.categories;
    }

    series = convertPeerToNationalAverage(series, convertPeerToNational);

    const options = getChartOptions({
      ...baseParams,
      category: chartCategories,
      id: chart.id,
      name: chart.name,
      xaxistext: chart.xaxis || "",
      yaxistext: chart.yaxis,
      graphdataresponse: series,
    });

    graphListArray.push({
      name: chart.name,
      id: chart.id,
      option: options,
      series: series,
    });
  });

  // Annual Cull Chart
  let annualCullSeries = graphObject
    ? ""
    : graphResult.annual_cull_and_death_rates[0].Culling_data;
  annualCullSeries = convertPeerToNationalAverage(
    annualCullSeries,
    convertPeerToNational,
  );

  const annualCullOptions = getChartOptions({
    ...baseParams,
    category: graphObject
      ? ""
      : graphResult.annual_cull_and_death_rates[0].categories,
    id: CHART_IDS.ANNUAL_CULL,
    name: "Annual Cull",
    xaxistext: "",
    yaxistext: "Percent",
    graphdataresponse: annualCullSeries,
  });

  graphListArray.push({
    name: "Annual Cull",
    id: CHART_IDS.ANNUAL_CULL,
    option: annualCullOptions,
    series: annualCullSeries,
  });

  // Lactation Curve (not for special tabs)
  if (
    ![
      "cullinghistory",
      "snapsix",
      "periodcomparison",
      "subherdcomparison",
    ].includes(reportTab)
  ) {
    let lactationCurveSeries = curveChart ? curveChart.Goal_data : null;
    let lactationCurveCategories = curveChart ? curveChart.categories : "";
    let rawCategories = lactationCurveCategories;

    if (
      curveChart &&
      reportTab !== "benchmark" &&
      (useavgdailymilkflag || reportResponse?.Use_Avg_Daily_Milk)
    ) {
      // Use categories from reportResponse.Lacation_graph_values and convert to numeric
      rawCategories =
        reportResponse?.Lacation_graph_values?.categories ||
        lactationCurveCategories;
      lactationCurveCategories = Array.isArray(rawCategories)
        ? rawCategories.map((cat) => {
            if (typeof cat === "string" && cat.startsWith("Week ")) {
              const weekNumber = parseInt(cat.replace("Week ", ""), 10);
              return !isNaN(weekNumber) && weekNumber >= 1 && weekNumber <= 44
                ? weekNumber
                : cat;
            }
            return cat;
          })
        : WEEK_TICK_VALUES;
    }

    // Filter categories based on toggle (only for DC305 and DPLAN)
    // Only apply when using weeks in milk (not days in milk)
    // Exclude benchmark report only (include subherdcomparison to match snapshot behavior)
    if (
      showFullMilkWeeksToggle &&
      !useFullMilkWeeks &&
      reportTab !== "benchmark" &&
      (useavgdailymilkflag || reportResponse?.Use_Avg_Daily_Milk)
    ) {
      const allowedWeeks = [2, 4, 8, 15, 21, 25, 32, 37];

      // Convert categories to numeric if needed before filtering
      const numericCategories = Array.isArray(lactationCurveCategories)
        ? lactationCurveCategories.map((cat) => {
            if (typeof cat === "string" && cat.startsWith("Week ")) {
              const weekNumber = parseInt(cat.replace("Week ", ""), 10);
              return !isNaN(weekNumber) && weekNumber >= 1 && weekNumber <= 44
                ? weekNumber
                : cat;
            }
            return cat;
          })
        : lactationCurveCategories;

      // Store original categories for data filtering
      const originalCategories = numericCategories;

      // Filter categories to only allowed weeks
      lactationCurveCategories = numericCategories.filter((week) =>
        allowedWeeks.includes(week),
      );

      // Also filter the series data to match the filtered categories and convert to {x, y} format for numeric spacing
      if (lactationCurveSeries && Array.isArray(lactationCurveSeries)) {
        lactationCurveSeries = lactationCurveSeries.map((series) => {
          if (series.data && Array.isArray(series.data)) {
            const filteredData = [];
            series.data.forEach((value, idx) => {
              if (allowedWeeks.includes(originalCategories[idx])) {
                filteredData.push({
                  x: originalCategories[idx],
                  y: value,
                });
              }
            });

            return {
              ...series,
              data: filteredData,
            };
          }
          return series;
        });
      }
    }

    const lactationCurveOptions = getChartOptions({
      ...baseParams,
      chartType: "line",
      category: lactationCurveCategories,
      id: CHART_IDS.LACTATION_CURVE,
      name: "Test Day Milk Chart",
      xaxistext: xaxislabel,
      yaxistext: yaxislabel,
      graphdataresponse: lactationCurveSeries,
      useFilteredWeeks:
        showFullMilkWeeksToggle &&
        !useFullMilkWeeks &&
        reportTab !== "benchmark" &&
        (useavgdailymilkflag || reportResponse?.Use_Avg_Daily_Milk),
    });

    graphListArray.push({
      name: "Lactation Curve",
      id: CHART_IDS.LACTATION_CURVE,
      series: lactationCurveSeries,
      option: lactationCurveOptions,
    });
  }

  return graphListArray;
};
